import { useState } from 'preact/hooks';
import { api } from '../api.js';
import { household, goBack, showToast, friendlyError } from '../store.js';
import { __ } from '../i18n.js';
import { IMPACT_OPTIONS, PRIORITY_LEVELS } from '../lib/labels.js';
import { PROJECT_IMPORTANCE_OPTIONS, PROJECT_STATUSES } from '../lib/projects.js';
import BackButton from '../components/BackButton.jsx';

/**
 * HH-8: one form, no wizard — same "genuinely this simple" reasoning
 * SubscriptionFormScreen documents for itself (plan §4.5: "Honest
 * difficulty: low-medium. CRUD plus one relation."). Priority reuses
 * TaskFormScreen's own segmented-button styling (`.etm-priority-selector`)
 * since it's the exact same 1-4 scale.
 */
export default function ProjectFormScreen({ params }) {
  const project = params && params.project;
  const isEdit = Boolean(project && project.id);

  const [title, setTitle] = useState(project?.title || '');
  const [description, setDescription] = useState(project?.description || '');
  const [importance, setImportance] = useState(project?.importance || 2);
  const [impact, setImpact] = useState(project?.impact || 2);
  const [priority, setPriority] = useState(project?.priority || 2);
  const [status, setStatus] = useState(project?.status || 'idea');
  const [targetDate, setTargetDate] = useState(project?.target_date || '');
  // Scope choice with household preselected — offered on create only, same
  // reasoning as SubscriptionFormScreen's own isShared: the backend never
  // lets scope change after creation.
  const [isShared, setIsShared] = useState(!!household.value);
  const [saving, setSaving] = useState(false);

  function buildPayload() {
    return {
      title: title.trim(),
      description,
      importance: Number(importance),
      impact: Number(impact),
      priority: Number(priority),
      status,
      target_date: targetDate,
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      showToast(__('Geef het project een titel.', 'etm'), 'error');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.projects.update(project.id, buildPayload());
        showToast(__('Project opgeslagen.', 'etm'), 'success');
      } else {
        const payload = buildPayload();
        payload.is_shared = isShared;
        await api.projects.create(payload);
        showToast(__('Project toegevoegd.', 'etm'), 'success');
      }
      goBack();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    // The backend detaches referencing tasks rather than deleting them
    // (ETM_Projects::delete_project()) — say so, so nobody reads "verwijderen"
    // as "and every task in it too".
    if (!confirm(__('Project verwijderen? Taken in dit project blijven bestaan, maar verliezen hun koppeling.', 'etm'))) return;
    try {
      await api.projects.remove(project.id);
      showToast(__('Project verwijderd.', 'etm'), 'success');
      goBack();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    }
  }

  return (
    <div>
      <BackButton onClick={goBack} />
      <h1 class="etm-screen-title">{isEdit ? __('Project bewerken', 'etm') : __('Nieuw project', 'etm')}</h1>

      <form onSubmit={handleSubmit}>
        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-project-title">
            {__('Titel', 'etm')}
          </label>
          <input
            id="etm-project-title"
            class="etm-form-input"
            type="text"
            value={title}
            onInput={(e) => setTitle(e.target.value)}
            placeholder={__('Bijv. kozijnen schilderen', 'etm')}
            autofocus
            required
          />
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-project-description">
            {__('Omschrijving', 'etm')}
          </label>
          <textarea
            id="etm-project-description"
            class="etm-form-textarea"
            value={description}
            onInput={(e) => setDescription(e.target.value)}
            placeholder={__('Extra details (optioneel)', 'etm')}
          />
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label">{__('Status', 'etm')}</label>
          <div class="etm-priority-selector">
            {PROJECT_STATUSES.map((opt) => (
              <button
                key={opt.value}
                type="button"
                class={`etm-priority-btn${status === opt.value ? ' active' : ''}`}
                onClick={() => setStatus(opt.value)}
              >
                {opt.label()}
              </button>
            ))}
          </div>
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label">{__('Prioriteit', 'etm')}</label>
          <div class="etm-priority-selector">
            {PRIORITY_LEVELS.map((level) => (
              <button
                key={level.value}
                type="button"
                class={`etm-priority-btn${Number(priority) === level.value ? ' active' : ''}`}
                onClick={() => setPriority(level.value)}
              >
                {level.label()}
              </button>
            ))}
          </div>
        </div>

        <div class="etm-form-row">
          <div class="etm-form-group">
            <label class="etm-form-label" for="etm-project-importance">
              {__('Belangrijkheid', 'etm')}
            </label>
            <select
              id="etm-project-importance"
              class="etm-form-select"
              value={importance}
              onChange={(e) => setImportance(Number(e.target.value))}
            >
              {PROJECT_IMPORTANCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label()}
                </option>
              ))}
            </select>
          </div>
          <div class="etm-form-group">
            <label class="etm-form-label" for="etm-project-impact">
              {__('Impact', 'etm')}
            </label>
            <select
              id="etm-project-impact"
              class="etm-form-select"
              value={impact}
              onChange={(e) => setImpact(Number(e.target.value))}
            >
              {IMPACT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label()}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-project-target-date">
            {__('Streefdatum', 'etm')}
          </label>
          <input
            id="etm-project-target-date"
            class="etm-form-input"
            type="date"
            value={targetDate}
            onInput={(e) => setTargetDate(e.target.value)}
          />
          <p class="etm-field-hint">{__('Optioneel — leeg laten als er geen richtdatum is.', 'etm')}</p>
        </div>

        {!isEdit && household.value ? (
          <div class="etm-form-group etm-checkbox-group">
            <input
              id="etm-project-shared"
              type="checkbox"
              checked={isShared}
              onChange={(e) => setIsShared(e.currentTarget.checked)}
            />
            <label for="etm-project-shared">{__('Gedeeld met huishouden', 'etm')}</label>
          </div>
        ) : null}

        <button type="submit" class="etm-btn etm-btn-primary" disabled={saving}>
          {isEdit ? __('Opslaan', 'etm') : __('Project toevoegen', 'etm')}
        </button>
      </form>

      {isEdit ? (
        <button type="button" class="etm-btn etm-btn-danger" onClick={handleDelete}>
          {__('Verwijderen', 'etm')}
        </button>
      ) : null}
    </div>
  );
}
